// ═══════════════════════════════════════════════════════════════════════════════════════════════════
// ModelSelectionCard — Agent Studio UI for the per-agent model binding
// ═══════════════════════════════════════════════════════════════════════════════════════════════════
//
// Lives inside the "Model selection" SectionCard on the agent detail page's
// AI Brain tab (the parent owns the card chrome; this component owns the body).
// Three goals, same as the Reasoning effort card it is modelled on:
//
//   1. AUTHORS SEE WHAT THEY'RE PICKING — every option shows its deployment,
//      provider, chat/process enablement, capabilities, and whether a Foundry
//      variant of this agent has been provisioned for it. Visible while choosing.
//
//   2. CAVEATS ARE NEVER SWALLOWED — the server's `warning` (on load and on
//      save) is rendered as a persistent Alert. It informs; it does not block.
//
//   3. CHANGES ARE AUDITED — picking a different model opens a confirm that
//      asks for a reason and says plainly that a model change is an accuracy
//      event. The audit trail is one click away ("Recent changes").
//
// Backed by /api/AgentModel/* endpoints (side-car to ai.AiAgent.ModelId).
// ═══════════════════════════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Drawer,
  Empty,
  Input,
  Modal,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  ApiOutlined,
  CheckCircleFilled,
  CloudOutlined,
  EyeOutlined,
  HistoryOutlined,
  ThunderboltFilled,
  WarningFilled,
} from '@ant-design/icons';
import {
  getAgentModel,
  getAgentModelHistory,
  getAgentModelOptions,
  setAgentModel,
  type AgentModelHistoryRow,
  type AgentModelOption,
  type AgentModelState,
} from '@/api/agentModel';
import { colors } from '@/theme/tokens';

const { Text, Paragraph } = Typography;

const MONO = "'JetBrains Mono', monospace";

// ────────────────────────────────────────────────────────────────────────────
// Main component
// ────────────────────────────────────────────────────────────────────────────

interface Props {
  agentId: number;
  /** Optional dark-mode toggle so colors invert correctly. */
  isDark?: boolean;
}

export function ModelSelectionCard({ agentId, isDark = false }: Props) {
  const [state, setState] = useState<AgentModelState | null>(null);
  const [options, setOptions] = useState<AgentModelOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  /** Non-blocking caveat returned by the last Set call. Sticks until the next save or unmount. */
  const [saveWarning, setSaveWarning] = useState<string | null>(null);

  // ── Load (initial + after save) ─────────────────────────────────────
  const load = useCallback(async (): Promise<void> => {
    const [st, opts] = await Promise.all([
      getAgentModel(agentId),
      getAgentModelOptions(agentId),
    ]);
    setState(st);
    setOptions(opts);
  }, [agentId]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setSaveWarning(null);
      try {
        const [st, opts] = await Promise.all([
          getAgentModel(agentId),
          getAgentModelOptions(agentId),
        ]);
        if (cancelled) return;
        setState(st);
        setOptions(opts);
      } catch (e) {
        if (!cancelled) setError((e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId]);

  // ── Select handler — always confirms; model change is an accuracy event ──
  const handleSelect = async (opt: AgentModelOption) => {
    if (!state) return;
    if (opt.modelId === state.modelId) return; // no-op

    const reason = await confirmModelChange(state, opt);
    if (reason === null) return; // cancelled

    setSaving(true);
    setError(null);
    try {
      const result = await setAgentModel(agentId, {
        newModelId: opt.modelId,
        reason: reason || undefined,
      });
      setState(result.state);
      setSaveWarning(result.warning);
      void message.success({
        content: (
          <span>
            Model changed to <strong>{result.state.modelTitle || result.state.modelName}</strong>
            {result.warning ? ' — see the note on the card.' : ''}
          </span>
        ),
        duration: 5,
      });
      // Refresh options so isCurrent + provisioning flags reflect the new binding.
      try {
        await load();
      } catch (e) {
        void message.warning('Saved, but refreshing the model list failed: ' + (e as Error).message);
      }
    } catch (e) {
      void message.error('Save failed: ' + (e as Error).message);
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div style={{ padding: 40, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (error && !state) {
    return <Alert type="error" showIcon message={error} />;
  }

  if (!state) {
    return <Alert type="error" showIcon message="Failed to load." />;
  }

  return (
    <>
      {/* ── Header row — actions live here because the parent SectionCard owns the title ── */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 8,
          marginBottom: 12,
        }}
      >
        <Text type="secondary" style={{ fontSize: 12.5 }}>
          The agent runs every turn on the model below. Pick another card to change it.
        </Text>
        <Button size="small" icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
          Recent changes
        </Button>
      </div>

      {/* ── Server caveat about the current binding ─────────────────── */}
      {state.warning && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningFilled />}
          message="Check this model binding"
          description={state.warning}
          style={{ marginBottom: 14 }}
        />
      )}

      {/* ── Non-blocking caveat from the last save ─────────────────── */}
      {saveWarning && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningFilled />}
          message="Model changed, with a note"
          description={saveWarning}
          style={{ marginBottom: 14 }}
        />
      )}

      {/* ── Save error (state still loaded) ────────────────────────── */}
      {error && (
        <Alert type="error" showIcon message={error} closable onClose={() => setError(null)} style={{ marginBottom: 14 }} />
      )}

      {/* ── Current model — prominent ──────────────────────────────── */}
      <CurrentModelPanel state={state} isDark={isDark} />

      {/* ── Option cards ───────────────────────────────────────────── */}
      <Text
        strong
        style={{
          display: 'block',
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: 0.4,
          margin: '16px 0 8px',
          color: isDark ? colors.slate[400] : colors.slate[500],
        }}
      >
        Available models
      </Text>
      {options.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No models are available to this agent. Add one on the Models page first."
        />
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
            gap: 12,
          }}
        >
          {options.map((o) => (
            <ModelOptionCard
              key={o.modelId}
              option={o}
              isSelected={o.modelId === state.modelId}
              isSaving={saving}
              isDark={isDark}
              onSelect={() => void handleSelect(o)}
            />
          ))}
        </div>
      )}

      <HistoryDrawer agentId={agentId} open={historyOpen} onClose={() => setHistoryOpen(false)} />
    </>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Provider badge — "Azure Foundry" when the model is served through a Foundry
// agent, otherwise the API's title. Shared by the current panel + option cards.
// ────────────────────────────────────────────────────────────────────────────

function ProviderBadge({ useFoundryAgent, apiTitle }: { useFoundryAgent: boolean; apiTitle: string | null }) {
  if (useFoundryAgent) {
    return (
      <Tag icon={<CloudOutlined />} color="purple" style={{ margin: 0 }}>
        Azure Foundry
      </Tag>
    );
  }
  return (
    <Tag icon={<ApiOutlined />} style={{ margin: 0 }}>
      {apiTitle || 'Unknown provider'}
    </Tag>
  );
}

function ProvisionedTag({ provisioned, useFoundryAgent }: { provisioned: boolean; useFoundryAgent: boolean }) {
  if (provisioned) {
    return (
      <Tooltip title="A Foundry version of this agent exists for this model — switching is immediate.">
        <Tag color="green" style={{ margin: 0 }}>
          Foundry version provisioned
        </Tag>
      </Tooltip>
    );
  }
  return (
    <Tooltip
      title={
        useFoundryAgent
          ? 'No Foundry version of this agent exists for this model yet. It will need provisioning after you switch.'
          : 'This model does not run through a Foundry agent, so no Foundry version is needed.'
      }
    >
      <Tag style={{ margin: 0, color: colors.slate[500], borderStyle: 'dashed' }}>
        {useFoundryAgent ? 'Foundry version not provisioned' : 'No Foundry version needed'}
      </Tag>
    </Tooltip>
  );
}

function EnablementTags({
  enableInChat,
  enableInProcess,
  supportsReasoningEffort,
  supportsVision,
}: {
  enableInChat: boolean;
  enableInProcess: boolean;
  supportsReasoningEffort: boolean;
  supportsVision: boolean;
}) {
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      <Tag color={enableInChat ? 'blue' : undefined} style={{ margin: 0, fontSize: 11 }}>
        {enableInChat ? 'chat' : 'chat off'}
      </Tag>
      <Tag color={enableInProcess ? 'blue' : undefined} style={{ margin: 0, fontSize: 11 }}>
        {enableInProcess ? 'process' : 'process off'}
      </Tag>
      {supportsReasoningEffort && (
        <Tag icon={<ThunderboltFilled />} color="geekblue" style={{ margin: 0, fontSize: 11 }}>
          reasoning
        </Tag>
      )}
      {supportsVision && (
        <Tag icon={<EyeOutlined />} color="cyan" style={{ margin: 0, fontSize: 11 }}>
          vision
        </Tag>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Current model panel — the prominent "this is what runs" block
// ────────────────────────────────────────────────────────────────────────────

function CurrentModelPanel({ state, isDark }: { state: AgentModelState; isDark: boolean }) {
  const accent = state.modelActive ? colors.violet[600] : colors.amber[600];
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 14,
        padding: '14px 16px',
        borderRadius: 10,
        border: `2px solid ${accent}`,
        background: isDark ? colors.slate[800] : state.modelActive ? colors.violet[50] : colors.amber[100],
        boxShadow: `0 2px 12px ${accent}33`,
      }}
    >
      <CheckCircleFilled style={{ color: accent, fontSize: 22, marginTop: 2 }} />
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Text
            style={{
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.4,
              color: isDark ? colors.slate[400] : colors.slate[500],
            }}
          >
            Current model
          </Text>
          <ProviderBadge useFoundryAgent={state.useFoundryAgent} apiTitle={state.apiTitle} />
          {!state.modelActive && (
            <Tag color="orange" style={{ margin: 0 }}>
              model inactive
            </Tag>
          )}
        </div>
        <Text
          style={{
            fontSize: 18,
            fontWeight: 700,
            color: isDark ? colors.slate[50] : colors.slate[900],
            lineHeight: 1.25,
          }}
        >
          {state.modelTitle || state.modelName}
        </Text>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <Text style={{ fontSize: 12, fontFamily: MONO, color: isDark ? colors.slate[300] : colors.slate[600] }}>
            {state.modelName}
            {state.deploymentName ? ` · ${state.deploymentName}` : ''}
            {' · #'}
            {state.modelId}
          </Text>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <EnablementTags
            enableInChat={state.enableInChat}
            enableInProcess={state.enableInProcess}
            supportsReasoningEffort={state.supportsReasoningEffort}
            supportsVision={state.supportsVision}
          />
          <ProvisionedTag provisioned={state.foundryVariantProvisioned} useFoundryAgent={state.useFoundryAgent} />
        </div>
        {state.foundryVariantAgentRef && (
          <Text style={{ fontSize: 10.5, fontFamily: MONO, color: isDark ? colors.slate[500] : colors.slate[400] }}>
            foundry ref: <code>{state.foundryVariantAgentRef}</code>
          </Text>
        )}
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Option card — one per model in the picker grid
// ────────────────────────────────────────────────────────────────────────────

function ModelOptionCard({
  option: o,
  isSelected,
  isSaving,
  isDark,
  onSelect,
}: {
  option: AgentModelOption;
  isSelected: boolean;
  isSaving: boolean;
  isDark: boolean;
  onSelect: () => void;
}) {
  const accent = colors.violet[600];
  const idleBorder = isDark ? colors.slate[700] : colors.slate[200];
  return (
    <button
      type="button"
      onClick={onSelect}
      disabled={isSaving || isSelected}
      aria-pressed={isSelected}
      style={{
        all: 'unset',
        cursor: isSaving ? 'wait' : isSelected ? 'default' : 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        padding: 14,
        borderRadius: 10,
        border: `2px solid ${isSelected ? accent : idleBorder}`,
        background: isSelected ? (isDark ? colors.slate[800] : colors.violet[50]) : isDark ? colors.slate[800] : '#FFFFFF',
        transition: 'all 0.15s ease',
        position: 'relative',
        boxShadow: isSelected ? `0 2px 12px ${accent}33` : 'none',
      }}
      onMouseEnter={(e) => {
        if (!isSelected && !isSaving) {
          e.currentTarget.style.borderColor = accent;
        }
      }}
      onMouseLeave={(e) => {
        if (!isSelected) {
          e.currentTarget.style.borderColor = idleBorder;
        }
      }}
    >
      {isSelected && (
        <CheckCircleFilled
          style={{
            position: 'absolute',
            top: 10,
            right: 10,
            color: accent,
            fontSize: 18,
          }}
        />
      )}

      {/* Header — title + provider */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, paddingRight: isSelected ? 24 : 0 }}>
        <Text
          style={{
            fontSize: 15,
            fontWeight: 700,
            color: isSelected ? accent : isDark ? colors.slate[100] : colors.slate[900],
          }}
        >
          {o.title || o.name}
        </Text>
        <Text style={{ fontSize: 11.5, fontFamily: MONO, color: isDark ? colors.slate[400] : colors.slate[500] }}>
          {o.name}
          {o.deploymentName ? ` · ${o.deploymentName}` : ''}
        </Text>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <ProviderBadge useFoundryAgent={o.useFoundryAgent} apiTitle={o.apiTitle} />
        {o.merchantId == null ? (
          <Tag style={{ margin: 0, fontSize: 11 }}>platform</Tag>
        ) : (
          <Tag style={{ margin: 0, fontSize: 11 }}>merchant #{o.merchantId}</Tag>
        )}
      </div>

      <EnablementTags
        enableInChat={o.enableInChat}
        enableInProcess={o.enableInProcess}
        supportsReasoningEffort={o.supportsReasoningEffort}
        supportsVision={o.supportsVision}
      />

      <div style={{ marginTop: 'auto' }}>
        <ProvisionedTag provisioned={o.foundryVariantProvisioned} useFoundryAgent={o.useFoundryAgent} />
      </div>
    </button>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// History drawer — audit trail
// ────────────────────────────────────────────────────────────────────────────

function HistoryDrawer({
  agentId,
  open,
  onClose,
}: {
  agentId: number;
  open: boolean;
  onClose: () => void;
}) {
  const [rows, setRows] = useState<AgentModelHistoryRow[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const data = await getAgentModelHistory(agentId);
        if (!cancelled) setRows(data);
      } catch (e) {
        if (!cancelled) void message.error('Load history failed: ' + (e as Error).message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentId, open]);

  return (
    <Drawer
      title="Model — change history"
      placement="right"
      width={520}
      open={open}
      onClose={onClose}
      destroyOnHidden
    >
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Spin />
        </div>
      ) : rows.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="No changes yet — this agent is on its original model."
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {rows.map((r) => (
            <HistoryRow key={r.id} row={r} />
          ))}
        </div>
      )}
    </Drawer>
  );
}

function HistoryRow({ row }: { row: AgentModelHistoryRow }) {
  const when = new Date(row.changedAtUtc);
  return (
    <div
      style={{
        padding: 12,
        border: `1px solid ${colors.slate[200]}`,
        borderRadius: 8,
        background: '#FFFFFF',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        {row.oldModelName ? (
          <>
            <Tag>{row.oldModelName}</Tag>
            <Text type="secondary">→</Text>
            <Tag color="purple">{row.newModelName}</Tag>
          </>
        ) : (
          <>
            <Tag color="purple">{row.newModelName}</Tag>
            <Text type="secondary" style={{ fontSize: 11.5 }}>(initial set)</Text>
          </>
        )}
      </div>
      {row.reason && (
        <Paragraph style={{ margin: '4px 0', fontSize: 12.5, color: colors.slate[700] }}>
          <Text type="secondary">Reason: </Text>
          {row.reason}
        </Paragraph>
      )}
      <Text type="secondary" style={{ fontSize: 11.5 }}>
        by user #{row.changedByUserId} · {when.toLocaleString()}
      </Text>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Change confirm — Modal.confirm with an optional reason textarea.
// Returns Promise<string | null> — null if cancelled, '' if no reason given.
// ────────────────────────────────────────────────────────────────────────────

function confirmModelChange(current: AgentModelState, next: AgentModelOption): Promise<string | null> {
  const fromLabel = current.modelTitle || current.modelName;
  const toLabel = next.title || next.name;
  return new Promise<string | null>((resolve) => {
    let reasonValue = '';
    Modal.confirm({
      title: `Change model: ${fromLabel} → ${toLabel}?`,
      icon: <WarningFilled style={{ color: colors.amber[500] }} />,
      width: 540,
      content: (
        <div>
          <Paragraph style={{ marginTop: 8 }}>
            Changing the model is an accuracy event. The same instructions, skills, and
            knowledge can produce different answers on a different model — re-run this
            agent's validation scenarios after switching. Please record why you're making
            this change (optional, but it goes in the audit trail).
          </Paragraph>
          {next.useFoundryAgent && !next.foundryVariantProvisioned && (
            <Alert
              type="warning"
              showIcon
              message="No Foundry version of this agent exists for this model yet."
              description="It will need provisioning after you switch. Chat may not work on the new model until then."
              style={{ marginBottom: 10 }}
            />
          )}
          {!next.enableInChat && (
            <Alert
              type="warning"
              showIcon
              message="This model is not enabled for chat."
              style={{ marginBottom: 10 }}
            />
          )}
          <Input.TextArea
            placeholder={'e.g. "Moving to the newer deployment after the 2026-09 validation run passed on it."'}
            rows={3}
            onChange={(e) => {
              reasonValue = e.target.value;
            }}
            autoFocus
          />
        </div>
      ),
      okText: 'Change model',
      okType: 'primary',
      cancelText: 'Cancel',
      onOk: () => resolve(reasonValue.trim()),
      onCancel: () => resolve(null),
    });
  });
}

export default ModelSelectionCard;
